/**
 * WeatherHourlyPresenter - Hourly forecast bars presenter
 *
 * NOTE: This presenter component is separated from WeatherHourlyWidget.tsx for Module Federation.
 * It contains the same UI logic but receives all data via props instead of MobX injection.
 * This allows JHP to consume it via Module Federation while Zoomin's WeatherHourlyWidget.tsx
 * continues to use MobX stores for data. Both components should render identically.
 */

import * as React from 'react'

import { classList } from 'react-classlist-helper'

import WeatherBarPresenter, { IWeatherBarForecast } from './WeatherBarPresenter'

const MIN_BAR_HEIGHT = 124
const MAX_BAR_HEIGHT = 160

export interface IWeatherHourlyPresenterProps {
  /** Hourly forecasts to display */
  forecasts: IWeatherBarForecast[]
  /** Display units */
  units: 'imperial' | 'metric'
  /** Format time for display - returns [time, period] e.g. ["3", "PM"] */
  formatTime?: (timestamp: number) => [string, string]
  /** Icon size in pixels */
  iconSize?: number
  /** Maximum bar height in pixels */
  maxBarHeight?: number
  /** Minimum bar height in pixels */
  minBarHeight?: number
  /** Additional CSS class name */
  className?: string
  /** Whether it's constrained horizontally */
  compactH?: boolean
  /** Whether it's constrained vertically */
  compactV?: boolean
}

export default function WeatherHourlyPresenter({
  forecasts,
  units,
  formatTime,
  iconSize,
  maxBarHeight = MAX_BAR_HEIGHT,
  minBarHeight = MIN_BAR_HEIGHT,
  className,
  compactH,
  compactV,
}: IWeatherHourlyPresenterProps): JSX.Element {
  // Calculate height scaling based on temperature range
  const temperatures = forecasts.map(f => f.temperature)
  const minTemp = Math.min(...temperatures)
  const maxTemp = Math.max(...temperatures)

  const delta =
    maxTemp !== minTemp
      ? (maxBarHeight - minBarHeight) / (maxTemp - minTemp)
      : 0

  return (
    <div
      className={classList({
        'weather-hourly-widget py8': true,
        'gap-8': !compactH,
        'gap-4': compactH,
        [className || '']: !!className,
      })}
    >
      {forecasts.map((forecast, i) => {
        const height =
          maxTemp !== minTemp
            ? Math.round(
                maxBarHeight - delta * (maxTemp - forecast.temperature),
              )
            : maxBarHeight

        return (
          <WeatherBarPresenter
            key={i}
            forecast={forecast}
            units={units}
            height={height}
            iconSize={iconSize}
            formatTime={formatTime}
            barWidthClass={compactH ? 'w36' : 'w48'}
            compactH={compactH}
            compactV={compactV}
          />
        )
      })}
    </div>
  )
}
